/**
 * Global navbar search ("Szukaj badań, respondentów…").
 *
 * Fans the typed query out across the researcher's studies, address-book
 * respondents and question templates in parallel, hitting each resource's DRF
 * `?search=` endpoint, and folds the hits into one ranked, typed list the
 * top-bar dropdown can render. A failing resource degrades to no rows for that
 * group rather than failing the whole search.
 */

import { API_CONFIG } from '@/config/api'
import { baseService } from './baseService'

export type SearchResultType = 'study' | 'respondent' | 'template'

export interface SearchResult {
  type: SearchResultType
  id: number
  title: string
  subtitle?: string
  /** Lifecycle status for studies ('draft' | 'active'); drives both the badge and where we navigate. */
  status?: string
  /** Small right-aligned hint, e.g. "8 pytań" for templates. */
  meta?: string
}

interface ProjectLite {
  id: number
  name: string
  client?: string
  status?: string
}

interface RespondentLite {
  id: number
  email: string
  name?: string
}

interface TemplateLite {
  id: number
  name: string
  questions_count?: number
  is_hidden?: boolean
}

/** DRF gives us either a bare array (pagination off) or a `{results}` envelope. */
function unwrap<T>(data: T[] | { results?: T[] } | null | undefined): T[] {
  if (Array.isArray(data)) return data
  return data?.results ?? []
}

async function safeSearch<T>(endpoint: string, query: string): Promise<T[]> {
  const sep = endpoint.includes('?') ? '&' : '?'
  try {
    const res = await baseService.get<T[] | { results?: T[] }>(`${endpoint}${sep}search=${encodeURIComponent(query)}`)
    return unwrap<T>(res.data)
  } catch {
    // One resource failing (e.g. a 500) shouldn't blank the whole dropdown.
    return []
  }
}

/** Per-group cap so one noisy resource can't crowd the others out of the dropdown. */
const PER_GROUP_LIMIT = 6

export interface GlobalSearchOptions {
  /**
   * Whether to search the address book and the template library too. FaceWise
   * has neither room, so a hit there would be a result the reader cannot open —
   * the click would bounce off the route guard and read as a broken app.
   */
  includeProOnly?: boolean
}

export async function globalSearch(
  rawQuery: string,
  { includeProOnly = true }: GlobalSearchOptions = {},
): Promise<SearchResult[]> {
  const query = rawQuery.trim()
  if (query.length < 2) return []

  const [projects, respondents, templates] = await Promise.all([
    safeSearch<ProjectLite>(API_CONFIG.ENDPOINTS.PROJECTS.LIST, query),
    includeProOnly ? safeSearch<RespondentLite>(API_CONFIG.ENDPOINTS.RESPONDENTS.LIST, query) : [],
    includeProOnly
      ? safeSearch<TemplateLite>(API_CONFIG.ENDPOINTS.QUESTIONNAIRE_TEMPLATES.LIST, query)
      : [],
  ])

  const results: SearchResult[] = []

  for (const p of projects.slice(0, PER_GROUP_LIMIT)) {
    results.push({
      type: 'study',
      id: p.id,
      title: p.name,
      subtitle: p.client || undefined,
      status: p.status,
    })
  }

  for (const r of respondents.slice(0, PER_GROUP_LIMIT)) {
    const name = (r.name || '').trim()
    results.push({
      type: 'respondent',
      id: r.id,
      title: name || r.email,
      subtitle: name ? r.email : undefined,
    })
  }

  // Per-study questionnaires the wizard auto-creates are hidden from the library;
  // keep them out of search too so only reusable templates surface.
  for (const t of templates.filter((t) => !t.is_hidden).slice(0, PER_GROUP_LIMIT)) {
    results.push({
      type: 'template',
      id: t.id,
      title: t.name,
      meta: typeof t.questions_count === 'number' ? String(t.questions_count) : undefined,
    })
  }

  return results
}
